import { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import { cn } from '../../lib/utils';
import type { CollapseProps } from './types';

export const Collapse = forwardRef<HTMLDivElement, CollapseProps>(
  (
    {
      trigger,
      children,
      timeout = 0,
      open: controlledOpen,
      onOpenChange,
      unmountOnExit = false,
      defaultOpen,
      ...props
    },
    ref,
  ) => {
    const [internalOpen, setInternalOpen] = useState(!!defaultOpen);
    const isOpen = controlledOpen ?? internalOpen;
    const contentRef = useRef<HTMLDivElement>(null);

    const handleOpenChange = (next: boolean) => {
      if (controlledOpen === undefined) setInternalOpen(next);
      onOpenChange?.(next);
    };

    // Written to the DOM directly, not to state, so a toggle costs no extra
    // render. The keyframes clip the content, so scrollHeight includes the
    // children's margins, and it is the full height even at height 0.
    useLayoutEffect(() => {
      const el = contentRef.current;
      // Radix suppresses the animation on mount, and a `hidden` element never
      // runs one.
      if (!el || el.hidden || getComputedStyle(el).animationName === 'none') {
        return;
      }

      el.style.setProperty('--collapse-content-height', `${el.scrollHeight}px`);
    }, [isOpen]);

    return (
      <Collapsible.Root
        {...props}
        ref={ref}
        open={controlledOpen}
        defaultOpen={controlledOpen === undefined ? defaultOpen : undefined}
        onOpenChange={handleOpenChange}
      >
        {trigger && (
          <Collapsible.Trigger asChild>{trigger}</Collapsible.Trigger>
        )}

        {/* Without forceMount, Radix unmounts the children once the closing
            animation has finished. */}
        <Collapsible.Content
          ref={contentRef}
          forceMount={unmountOnExit ? undefined : true}
          style={
            timeout > 0 ? { animationDuration: `${timeout}ms` } : undefined
          }
          className={cn(
            // `invisible` also takes closed content out of the tab order.
            'eye:data-[state=closed]:invisible eye:data-[state=closed]:h-0 eye:data-[state=closed]:overflow-hidden',
            timeout > 0 &&
              'eye:motion-safe:data-[state=closed]:animate-collapse-up eye:motion-safe:data-[state=open]:animate-collapse-down',
          )}
        >
          {children}
        </Collapsible.Content>
      </Collapsible.Root>
    );
  },
);
